import { Box, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { OUTDATED_OS_DIALOG_NOTES } from 'constants/supportedOS';
import { Button, IconButton } from 'design-system';
import { FaDesktop, FaMobileAlt } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { isDesktopPlatform, OSPlatform, type OSInfo } from 'utils/osVersion';

const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  windows: 'Windows',
  macos: 'macOS',
};

const UnsupportedOSDialog = ({ open, onClose, osInfo }: { open: boolean; onClose: () => void; osInfo: OSInfo }) => {
  const platformLabel = PLATFORM_LABELS[osInfo.platform] ?? osInfo.platform;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: (theme) => ({
            borderRadius: 1,
            overflow: 'hidden',
            boxShadow: `0 24px 48px -12px rgba(0, 0, 0, 0.18), 0 0 0 1px ${theme.palette.divider}`,
          }),
        },
      }}
    >
      <Box
        sx={(theme) => ({
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          p: 3,
          position: 'relative',
        })}
      >
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={(theme) => ({
            position: 'absolute',
            top: 12,
            right: 12,
            color: theme.palette.common.white,
          })}
          track={false}
        >
          <MdClose size={24} />
        </IconButton>

        <Stack
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          <Stack
            sx={{
              flex: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            }}
          >
            {isDesktopPlatform(osInfo.platform) ? (
              <FaDesktop size={32} color="white" />
            ) : (
              <FaMobileAlt size={32} color="white" />
            )}
          </Stack>
          <Typography
            variant="h5"
            component="h2"
            sx={(theme) => ({
              color: theme.palette.common.white,
              fontWeight: theme.typography.fontWeightBold,
              textAlign: 'center',
            })}
          >
            Update Required
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              textAlign: 'center',
              maxWidth: 260,
            }}
          >
            Your {platformLabel} version is below the minimum we support. Please update
            {isDesktopPlatform(osInfo.platform) ? ' your operating system' : ' your device'} to get the best experience
            on the Nile platform.
          </Typography>
        </Stack>
      </Box>
      <DialogContent sx={{ py: 3, px: 3 }}>
        <Stack spacing={2.5}>
          <Box
            sx={(theme) => ({
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.grey[50],
              border: `1px solid ${theme.palette.divider}`,
            })}
          >
            <Box>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontWeight: theme.typography.fontWeightBold,
                })}
              >
                Your version
              </Typography>
              <Typography variant="h6" sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold, mt: 0.5 })}>
                {isDesktopPlatform(osInfo.platform) ? osInfo.versionString : `${platformLabel} ${osInfo.versionString}`}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontWeight: 600,
                })}
              >
                Minimum required
              </Typography>
              <Typography
                variant="h6"
                sx={(theme) => ({
                  fontWeight: theme.typography.fontWeightBold,
                  mt: 0.5,
                  color: theme.palette.primary.main,
                })}
              >
                {osInfo.minVersionString}
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              textAlign: 'center',
              lineHeight: 1.6,
            })}
          >
            You can close this message and continue, but some features may not work correctly. We recommend updating
            your {platformLabel} to the latest version for the best experience.
            {osInfo.platform === OSPlatform.Windows && ` ${OUTDATED_OS_DIALOG_NOTES.windows}`}
            {osInfo.platform === OSPlatform.MacOS && ` ${OUTDATED_OS_DIALOG_NOTES.macos}`}
          </Typography>

          <Button variant="contained" onClick={onClose}>
            Got it
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default UnsupportedOSDialog;
